import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  Clock,
  Gauge,
  PauseCircle,
  PlayCircle,
  BarChart3,
  AlertTriangle,
  XCircle,
  Hand,
  AlertOctagon,
  TrendingDown,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import apiService, { api } from '../services/api';
import toast from 'react-hot-toast';
import { useMachineData } from '../hooks/useMachineData';
import { useMachineStatusConfig } from '../hooks/useMachineStatusConfig';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { MACHINE_CONSTANTS } from '../constants/machineConstants';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Machine {
  id: number;
  number: string;
  machineType?: 'water_jacket' | 'rapier_jacket';
  groupId?: number;
  group?: {
    id: number;
    name: string;
    description?: string;
  };
  productions?: Array<{
    id: number;
    meters: number;
    metersProduced: number;
    status: string;
    beam?: {
      id: number;
      number: string;
      totalMeters: number;
    };
    quality?: {
      id: number;
      name: string;
    };
  }>;
  pickValue: number;
  isOn: boolean;
  efficiency: number;
  efficiencyThreshold?: number;
  onlineHours?: number | string;
  totalMeters?: number;
  meters12HourDay?: number;
  meters12HourNight?: number;
  dayShiftEfficiency?: number;
  nightShiftEfficiency?: number;
  rpm?: number;
  reason?: string;
  stopCount?: number;
  lastUpdate?: string | null;
  isStale?: boolean;
  isHistorical?: boolean;
  currentStopStartedAt?: number | null;
  offlineHours?: string;
  calculatedEfficiency?: number;
  // WebSocket live data
  d2?: number; // Picks
  d5?: number;
}

const RapierMachine: React.FC = () => {
  const { user } = useAuth();
  const { resolveStatus } = useMachineStatusConfig();
  const [statusRange] = useState<'today' | 'lastDay'>('today');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['all']));
  const previousMachineStatesRef = useRef<Map<number, Machine>>(new Map());
  const isInitialLoadRef = useRef(true);

  // WebSocket data
  const { machineData } = useMachineData();

  // Get machine state from WebSocket data
  const getMachineState = (machineId: number) => {
    const allMachines = machineData as any[];
    if (!allMachines || allMachines.length === 0) return null;
    return allMachines.find((m: any) => m.machineId === machineId) || null;
  };

  // Fetch only rapier_jacket machines
  const { data: machinesResponse, isLoading } = useQuery({
    queryKey: ['machines', 'rapier_jacket'],
    queryFn: () => apiService.get('/machines?machineType=rapier_jacket'),
  });

  const machines = machinesResponse?.machines || [];

  // Expand all groups on initial load
  useEffect(() => {
    if (isInitialLoadRef.current && machines.length > 0) {
      setExpandedGroups(new Set(['all']));
      isInitialLoadRef.current = false;
    }
  }, [machines]);

  // Split machines into batches of 10
  const splitIntoBatches = (items: Machine[], batchSize: number = 10): Machine[][] => {
    const batches: Machine[][] = [];
    for (let i = 0; i < items.length; i += batchSize) {
      batches.push(items.slice(i, i + batchSize));
    }
    return batches;
  };

  const machineBatches = useMemo(() => {
    if (!machines || machines.length === 0) return [];
    return splitIntoBatches(machines, 10);
  }, [machines]);

  const statusPath = statusRange === 'lastDay' ? '/rapier-machine-logs/status/last-day' : '/rapier-machine-logs/status/all';

  // Batch queries for efficiency data (up to 5 batches for rapier machines)
  const batch0Query = useQuery({
    queryKey: ['rapier-efficiency-batch', statusRange, 0, machineBatches[0]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}?machineIds=${machineBatches[0]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 0 && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch1Query = useQuery({
    queryKey: ['rapier-efficiency-batch', statusRange, 1, machineBatches[1]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}?machineIds=${machineBatches[1]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 1 && batch0Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch2Query = useQuery({
    queryKey: ['rapier-efficiency-batch', statusRange, 2, machineBatches[2]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}?machineIds=${machineBatches[2]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 2 && batch1Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch3Query = useQuery({
    queryKey: ['rapier-efficiency-batch', statusRange, 3, machineBatches[3]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}?machineIds=${machineBatches[3]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 3 && batch2Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batch4Query = useQuery({
    queryKey: ['rapier-efficiency-batch', statusRange, 4, machineBatches[4]?.map(m => m.id).join(',') || ''],
    queryFn: () => apiService.get(`${statusPath}?machineIds=${machineBatches[4]?.map(m => m.id).join(',') || ''}`),
    enabled: machineBatches.length > 4 && batch3Query.isSuccess && machines.length > 0,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const batchQueries = [batch0Query, batch1Query, batch2Query, batch3Query, batch4Query].slice(0, machineBatches.length);

  // Merge all batch results
  const machinesEfficiency = useMemo(() => {
    const allResults: any[] = [];
    batchQueries.forEach(query => {
      if (query.data?.data && Array.isArray(query.data.data)) {
        allResults.push(...query.data.data);
      }
    });
    return allResults;
  }, [batchQueries.map(q => q.data)]);

  // Merge machines with efficiency data and WebSocket data
  const machinesWithData = useMemo(() => {
    if (!machines || machines.length === 0) return [];

    return machines.map((machine: Machine) => {
      const efficiencyData = machinesEfficiency.find((e: any) => e.id === machine.id);
      const wsState = getMachineState(machine.id);
      const isHistorical = !!efficiencyData?.isHistorical;

      const efficiency = efficiencyData?.efficiency ?? (machine.efficiency || 0);
      const totalMeters = efficiencyData?.totalMeters ?? (machine.totalMeters || 0);
      const meters12HourDay = efficiencyData?.meters12HourDay ?? (machine.meters12HourDay || 0);
      const meters12HourNight = efficiencyData?.meters12HourNight ?? (machine.meters12HourNight || 0);
      const onlineHours = efficiencyData?.onlineHours ?? (machine.onlineHours || 0);
      const dayShiftEfficiency = efficiencyData?.dayShiftEfficiency ?? (machine.dayShiftEfficiency || 0);
      const nightShiftEfficiency = efficiencyData?.nightShiftEfficiency ?? (machine.nightShiftEfficiency || 0);
      const efficiencyThreshold = efficiencyData?.efficiencyThreshold ?? (machine.efficiencyThreshold ?? 95);

      const rpm = isHistorical
        ? (efficiencyData?.rpm ?? machine.rpm ?? 0)
        : (wsState?.RPM || 0);

      // Staleness check
      const now = Date.now();
      let wsTimestamp = 0;
      if (wsState?.timestamp) {
        const parsed = dayjs.utc(wsState.timestamp);
        if (parsed.isValid()) {
          wsTimestamp = parsed.subtract(5.5, 'hour').valueOf();
        }
      }
      let apiLastUpdate = 0;
      if (efficiencyData?.lastUpdate) {
        const parsed = dayjs.utc(efficiencyData.lastUpdate);
        if (parsed.isValid()) {
          apiLastUpdate = parsed.subtract(5.5, 'hour').valueOf();
        }
      }
      const lastUpdateTime = Math.max(wsTimestamp, apiLastUpdate);

      let lastUpdateDisplay: string | null = null;
      if (lastUpdateTime > 0) {
        const displaySource = wsTimestamp >= apiLastUpdate ? wsState?.timestamp : efficiencyData?.lastUpdate;
        if (displaySource) {
          const parsed = dayjs.utc(displaySource);
          if (parsed.isValid()) {
            lastUpdateDisplay = parsed.format("hh:mm:ss A");
          }
        }
      }

      const isStale = isHistorical
        ? false
        : (lastUpdateTime === 0 || (now - lastUpdateTime) > (60 * 1000 * 2));

      let isOn = wsState !== null && wsState !== undefined
        ? (isStale ? false : wsState.isOnline)
        : (isStale ? false : machine.isOn);

      const newReason = isStale ? 'Offline' : (wsState?.reason || 'ignored');

      // Preserve previous state for 'ignored' reason
      const previousState = previousMachineStatesRef.current.get(machine.id);
      if (newReason === 'ignored' && previousState) {
        return {
          ...previousState,
          efficiency,
          totalMeters,
          meters12HourDay,
          meters12HourNight,
          onlineHours,
          dayShiftEfficiency,
          nightShiftEfficiency,
          efficiencyThreshold,
          stopCount: efficiencyData?.stopCount || 0,
          currentStopStartedAt: efficiencyData?.currentStopStartedAt ?? null,
          offlineHours: efficiencyData?.offlineHours, // no-signal time only (never stopped time); card shows '0h 0m' when missing
          lastUpdate: lastUpdateDisplay ?? previousState.lastUpdate,
        };
      }

      const updatedMachine: Machine = {
        ...machine,
        efficiency,
        totalMeters,
        meters12HourDay,
        meters12HourNight,
        onlineHours,
        dayShiftEfficiency,
        nightShiftEfficiency,
        efficiencyThreshold,
        rpm,
        isOn,
        reason: newReason,
        lastUpdate: lastUpdateDisplay,
        stopCount: efficiencyData?.stopCount || 0,
        currentStopStartedAt: efficiencyData?.currentStopStartedAt ?? null,
        offlineHours: efficiencyData?.offlineHours, // no-signal time only (never stopped time); card shows '0h 0m' when missing
        isStale,
        isHistorical,
        d2: wsState?.D2 || 0,
        d5: wsState?.D5 || 0,
      };

      previousMachineStatesRef.current.set(machine.id, updatedMachine);
      return updatedMachine;
    });
  }, [machines, machinesEfficiency, machineData]);

  // Get machine status
  const getMachineStatus = (machine: Machine) => {
    const efficiency = machine.efficiency || 0;
    const reason = machine.reason || 'ignored';
    const threshold = machine.efficiencyThreshold || 95;
    const resolved = resolveStatus(efficiency, machine.isOn, machine.isStale, reason, threshold);
    return { text: resolved.statusText, color: resolved.color };
  };

  // Calculate shift duration and runtime from shift start
  const getShiftInfo = () => {
    const now = dayjs().tz(MACHINE_CONSTANTS.TIMEZONE);
    let shiftStart;
    if (now.hour() < MACHINE_CONSTANTS.SHIFT_START_HOUR) {
      shiftStart = now.subtract(1, 'day').hour(MACHINE_CONSTANTS.SHIFT_START_HOUR).minute(0).second(0);
    } else {
      shiftStart = now.hour(MACHINE_CONSTANTS.SHIFT_START_HOUR).minute(0).second(0);
    }
    const durationMinutes = now.diff(shiftStart, 'minute');
    const durationH = Math.floor(durationMinutes / 60);
    const durationM = durationMinutes % 60;
    return {
      duration: `${String(durationH).padStart(2, '0')}:${String(durationM).padStart(2, '0')}`,
      shiftStart,
    };
  };

  // Format time from seconds or hours string
  const formatRuntime = (onlineHours: number | string | undefined) => {
    if (!onlineHours) return '00:00';
    if (typeof onlineHours === 'string') return onlineHours;
    const h = Math.floor(onlineHours);
    const m = Math.floor((onlineHours - h) * 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  // Get beam left meters from production
  const getBeamLeft = (machine: Machine) => {
    if (!machine.productions || machine.productions.length === 0) return 0;
    const activeProduction = machine.productions.find(p => p.status === 'active') || machine.productions[0];
    if (activeProduction?.beam) {
      const remaining = activeProduction.beam.totalMeters - (activeProduction.metersProduced || 0);
      return Math.max(0, Math.round(remaining));
    }
    return 0;
  };

  // Status counts
  const statusCounts = useMemo(() => {
    if (!machinesWithData || machinesWithData.length === 0) {
      return { running: 0, stopped: 0, fault: 0, manual: 0, total: 0, avgEfficiency: 0, totalPicks: 0, avgSpeed: 0, totalMeters: 0 };
    }
    let running = 0, stopped = 0, fault = 0, manual = 0;
    let totalEfficiency = 0, efficiencyCount = 0, totalPicks = 0, totalSpeed = 0, speedCount = 0, totalMeters = 0;

    machinesWithData.forEach((machine: Machine) => {
      const status = getMachineStatus(machine);
      if (status.text === 'Running' || status.text === 'Low Efficiency' || status.text === 'Poor Efficiency' || status.text === 'Problem') running++;
      else if (status.text === 'Fault') fault++;
      else if (status.text === 'Manual') manual++;
      else stopped++;

      if (machine.efficiency !== undefined && machine.efficiency !== null) {
        totalEfficiency += machine.efficiency;
        efficiencyCount++;
      }
      totalPicks += machine.d2 || 0;
      if (machine.rpm && machine.rpm > 0) {
        totalSpeed += machine.rpm;
        speedCount++;
      }
      totalMeters += machine.totalMeters || 0;
    });

    return {
      running,
      stopped,
      fault,
      manual,
      total: machinesWithData.length,
      avgEfficiency: efficiencyCount > 0 ? Math.round(totalEfficiency / efficiencyCount) : 0,
      totalPicks,
      avgSpeed: speedCount > 0 ? Math.round(totalSpeed / speedCount) : 0,
      totalMeters,
    };
  }, [machinesWithData]);

  const shiftInfo = getShiftInfo();

  // Group machines by their group
  const groupedMachines = useMemo(() => {
    const groups: Record<string, Machine[]> = {};
    machinesWithData.forEach((machine: Machine) => {
      const groupName = machine.group?.name || 'Ungrouped';
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(machine);
    });
    // Sort machines within each group by number
    Object.values(groups).forEach(group => {
      group.sort((a, b) => parseInt(a.number) - parseInt(b.number));
    });
    return groups;
  }, [machinesWithData]);

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  };

  // Stop events state per machine
  const [stopEventsMap, setStopEventsMap] = useState<Map<number, any>>(new Map());
  const [loadingStopEvents, setLoadingStopEvents] = useState<number | null>(null);

  const loadStopEvents = async (machineId: number) => {
    setLoadingStopEvents(machineId);
    try {
      const path = statusRange === 'lastDay'
        ? `/rapier-machine-logs/${machineId}/stop-events/last-day`
        : `/rapier-machine-logs/${machineId}/stop-events`;
      const response = await api.get(path);
      if (response.data.success) {
        setStopEventsMap(prev => {
          const next = new Map(prev);
          next.set(machineId, response.data.data);
          return next;
        });
      }
    } catch (error) {
      console.error('Error loading stop events:', error);
      toast.error('Failed to load stop events');
    } finally {
      setLoadingStopEvents(null);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-green-600"></div>
          <span className="text-sm text-gray-600 font-medium">Loading Rapier Machines...</span>
        </div>
      </div>
    );
  }

  if (machines.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-700">No Rapier Machines</h3>
          <p className="text-sm text-gray-500 mt-1">No rapier jacket machines found for this company.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6 py-4">

        {/* Top Summary Bar */}
        <div className="bg-white border border-gray-200 rounded-xl px-3 sm:px-5 py-3 mb-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            {/* Efficiency */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-50 border border-green-200">
              <Gauge className="w-4 h-4 text-green-600" />
              <span className="text-xs font-bold text-green-700 uppercase">Efficiency</span>
              <span className="text-sm font-black text-green-900">{statusCounts.avgEfficiency}%</span>
            </div>

            {/* Total Picks */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200">
              <BarChart3 className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-bold text-blue-700 uppercase">Picks</span>
              <span className="text-sm font-black text-blue-900 font-mono">{statusCounts.totalPicks.toLocaleString('en-IN')}</span>
            </div>

            {/* Avg Speed */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-cyan-50 border border-cyan-200">
              <Activity className="w-4 h-4 text-cyan-600" />
              <span className="text-xs font-bold text-cyan-700 uppercase">A.Speed</span>
              <span className="text-sm font-black text-cyan-900 font-mono">{statusCounts.avgSpeed}</span>
            </div>

            {/* Running */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-50 border border-green-200">
              <PlayCircle className="w-4 h-4 text-green-500" />
              <span className="text-xs font-bold text-green-700 uppercase">Running</span>
              <span className="text-sm font-black text-green-900">{statusCounts.running}</span>
            </div>

            {/* Stopped */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200">
              <PauseCircle className="w-4 h-4 text-red-500" />
              <span className="text-xs font-bold text-red-700 uppercase">Stopped</span>
              <span className="text-sm font-black text-red-900">{statusCounts.stopped + statusCounts.fault}</span>
            </div>

            {/* All */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
              <span className="text-xs font-bold text-gray-700 uppercase">All</span>
              <span className="text-sm font-black text-gray-900">{statusCounts.total}</span>
            </div>

            {/* Duration */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-50 border border-purple-200 ml-auto">
              <Clock className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-black text-purple-900 font-mono">{shiftInfo.duration}</span>
            </div>
          </div>
        </div>

        {/* Machine Cards Grid */}
        {Object.keys(groupedMachines).length === 1 && groupedMachines['Ungrouped'] ? (
          // No groups - just show all cards
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
            {machinesWithData.map((machine: Machine) => (
              <MachineCard
                key={machine.id}
                machine={machine}
                shiftDuration={shiftInfo.duration}
                getMachineStatus={getMachineStatus}
                getBeamLeft={getBeamLeft}
                formatRuntime={formatRuntime}
                stopEventsData={stopEventsMap.get(machine.id)}
                loadStopEvents={loadStopEvents}
                loadingStopEvents={loadingStopEvents}
              />
            ))}
          </div>
        ) : (
          // Grouped view
          Object.entries(groupedMachines).map(([groupName, groupMachines]) => (
            <div key={groupName} className="mb-4">
              <button
                onClick={() => toggleGroup(groupName)}
                className="flex items-center gap-2 px-4 py-2 mb-2 bg-white rounded-lg border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors w-full text-left"
              >
                {expandedGroups.has(groupName) ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
                <span className="text-lg font-bold text-gray-900">{groupName}</span>
                <span className="text-sm text-gray-500 ml-2">({groupMachines.length} machines)</span>
              </button>
              {expandedGroups.has(groupName) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                  {groupMachines.map((machine: Machine) => (
                    <MachineCard
                      key={machine.id}
                      machine={machine}
                      shiftDuration={shiftInfo.duration}
                      getMachineStatus={getMachineStatus}
                      getBeamLeft={getBeamLeft}
                      formatRuntime={formatRuntime}
                      stopEventsData={stopEventsMap.get(machine.id)}
                      loadStopEvents={loadStopEvents}
                      loadingStopEvents={loadingStopEvents}
                    />
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Individual Machine Card Component - matches ITSense layout
const MachineCard: React.FC<{
  machine: Machine;
  shiftDuration: string;
  getMachineStatus: (machine: Machine) => { text: string; color: string };
  getBeamLeft: (machine: Machine) => number;
  formatRuntime: (hours: number | string | undefined) => string;
  stopEventsData?: any;
  loadStopEvents: (machineId: number) => void;
  loadingStopEvents: number | null;
}> = ({ machine, shiftDuration, getMachineStatus, getBeamLeft, formatRuntime, stopEventsData, loadStopEvents, loadingStopEvents }) => {
  const status = getMachineStatus(machine);
  const efficiency = Math.max(0, Math.min(100, machine.efficiency || 0));
  const runtime = formatRuntime(machine.onlineHours);
  const beamLeft = getBeamLeft(machine);
  const picks = machine.d2 || 0;
  const speed = machine.rpm || 0;
  const meters = machine.totalMeters || 0;
  const stops = machine.stopCount || 0;

  // Get efficiency bar color
  const getEfficiencyBarColor = () => {
    if (efficiency >= 90) return '#16a34a'; // green
    if (efficiency >= 70) return '#eab308'; // yellow
    return '#dc2626'; // red
  };

  // Current stop indicator
  const isCurrentlyStopped = !machine.isOn;

  // Blink when stopped >= 15 min (tenant setting), until this user acknowledges that stop.
  const { user } = useAuth();
  const { config } = useMachineStatusConfig();
  const ackKey = `rapierStopAck:${user?.id ?? 'anon'}:${machine.id}`;
  const [ackedStop, setAckedStop] = useState<number | null>(() => {
    try {
      const v = localStorage.getItem(ackKey);
      return v ? Number(v) : null;
    } catch { return null; }
  });
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  const stopStart = machine.currentStopStartedAt ?? null;
  const shouldBlink =
    !!config?.highlightStoppedCards &&
    isCurrentlyStopped &&
    !machine.isStale &&
    (machine.reason || '').toLowerCase() !== 'offline' && // no-signal card never blinks (same as Machines.tsx)
    !machine.isHistorical &&
    stopStart != null &&
    nowTick - stopStart >= 15 * 60 * 1000 &&
    ackedStop !== stopStart;
  const acknowledgeStop = () => {
    if (stopStart == null) return;
    setAckedStop(stopStart);
    try { localStorage.setItem(ackKey, String(stopStart)); } catch { /* ignore */ }
  };

  // Stop event summary
  const stopSummary = useMemo(() => {
    if (!stopEventsData?.stopEvents) return null;
    const events = stopEventsData.stopEvents;
    let faultCount = 0, faultTime = 0;
    let manualCount = 0, manualTime = 0;
    let otherCount = 0, otherTime = 0;

    events.forEach((evt: any) => {
      const durationMs = evt.durationMs || 0;
      // Since backend doesn't differentiate Warp/Weft/Feeder, we show Fault vs Manual
      if (evt.reason === 'fault') {
        faultCount++;
        faultTime += durationMs;
      } else if (evt.reason === 'manual') {
        manualCount++;
        manualTime += durationMs;
      } else {
        otherCount++;
        otherTime += durationMs;
      }
    });

    const totalCount = faultCount + manualCount + otherCount;
    const totalTime = faultTime + manualTime + otherTime;

    const formatMs = (ms: number) => {
      const totalSec = Math.floor(ms / 1000);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    return {
      fault: { count: faultCount, time: formatMs(faultTime) },
      manual: { count: manualCount, time: formatMs(manualTime) },
      other: { count: otherCount, time: formatMs(otherTime) },
      total: { count: totalCount, time: formatMs(totalTime) },
    };
  }, [stopEventsData]);

  return (
    <div className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden${shouldBlink ? ' machine-card-stopped' : ''}`}>
      {/* Efficiency Header Bar */}
      <div className="relative px-3 py-2" style={{ backgroundColor: '#f0fdf4' }}>
        {/* Efficiency progress bar */}
        <div className="absolute bottom-0 left-0 h-1 transition-all duration-500" style={{
          width: `${efficiency}%`,
          backgroundColor: getEfficiencyBarColor(),
        }} />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg font-black text-gray-900 font-mono">M{machine.number}</span>
            {isCurrentlyStopped && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: status.color }}>
                {status.text === 'Manual' ? 'MAN' : status.text === 'Fault' ? 'FAULT' : 'STOP'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-lg font-black font-mono" style={{ color: getEfficiencyBarColor() }}>
              {efficiency}%
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-0.5 text-[10px] text-gray-500">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span className="font-semibold">{shiftDuration}</span>
            <span>Duration</span>
          </div>
          <div className="flex items-center gap-1">
            <PlayCircle className="w-3 h-3 text-green-600" />
            <span className="font-semibold">{runtime}</span>
            <span>Runtime</span>
          </div>
          <div className="flex items-center gap-1">
            <PauseCircle className="w-3 h-3 text-red-500" />
            <span className="font-semibold">{machine.offlineHours || '0h 0m'}</span>
            <span>OFFLINE HRS</span>
          </div>
        </div>
      </div>

      {/* Data Rows */}
      <div className="px-3 py-2 space-y-1.5">
        {/* Picks */}
        <div className="flex items-center justify-between py-1 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-xs font-semibold text-gray-600">Picks</span>
          </div>
          <span className="text-sm font-black font-mono text-gray-900">{picks.toLocaleString('en-IN')}</span>
        </div>

        {/* Speed */}
        <div className="flex items-center justify-between py-1 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-cyan-500" />
            <span className="text-xs font-semibold text-gray-600">Speed</span>
          </div>
          <span className="text-sm font-black font-mono text-gray-900">{speed}</span>
        </div>

        {/* Meters */}
        <div className="flex items-center justify-between py-1 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Gauge className="w-3.5 h-3.5 text-purple-500" />
            <span className="text-xs font-semibold text-gray-600">Mtrs</span>
          </div>
          <span className="text-sm font-black font-mono text-gray-900">{meters.toFixed(3)}</span>
        </div>

        {/* Stops (click = acknowledge current stop) */}
        <div onClick={acknowledgeStop} className="flex items-center justify-between py-1 border-b border-gray-100 cursor-pointer">
          <div className="flex items-center gap-2">
            <PauseCircle className="w-3.5 h-3.5 text-red-500" />
            <span className="text-xs font-semibold text-gray-600">Stops</span>
          </div>
          <span className="text-sm font-black font-mono text-gray-900">{stops}</span>
        </div>

        {/* Beam Left */}
        <div className="flex items-center justify-between py-1 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <svg className="w-3.5 h-3.5 text-orange-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <circle cx="12" cy="12" r="4" />
            </svg>
            <span className="text-xs font-semibold text-gray-600">Beam Left</span>
          </div>
          <span className="text-sm font-black font-mono text-gray-900">{beamLeft}</span>
        </div>

        {/* Set Picks */}
        <div className="flex items-center justify-between py-1 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <svg className="w-3.5 h-3.5 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="9" y1="3" x2="9" y2="21" />
              <line x1="15" y1="3" x2="15" y2="21" />
            </svg>
            <span className="text-xs font-semibold text-gray-600">Set Picks</span>
          </div>
          <span className="text-sm font-black font-mono text-gray-900">{machine.pickValue || 0}</span>
        </div>
      </div>

      {/* Stop Breakdown Table */}
      <div className="px-3 pb-2">
        {stopSummary ? (
          <div className="rounded border border-gray-200 overflow-hidden">
            <table className="w-full text-[10px]">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-2 py-1 text-left font-bold text-gray-600">Stops</th>
                  <th className="px-2 py-1 text-center font-bold text-gray-600">Fault</th>
                  <th className="px-2 py-1 text-center font-bold text-gray-600">Manual</th>
                  <th className="px-2 py-1 text-center font-bold text-gray-600">Other</th>
                  <th className="px-2 py-1 text-center font-bold text-white bg-gray-700">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-100">
                  <td className="px-2 py-1 font-bold text-gray-600">Count</td>
                  <td className="px-2 py-1 text-center font-bold text-gray-900">{stopSummary.fault.count}</td>
                  <td className="px-2 py-1 text-center font-bold text-gray-900">{stopSummary.manual.count}</td>
                  <td className="px-2 py-1 text-center font-bold text-gray-900">{stopSummary.other.count}</td>
                  <td className="px-2 py-1 text-center font-bold text-white bg-gray-700">{stopSummary.total.count}</td>
                </tr>
                <tr className="border-t border-gray-100">
                  <td className="px-2 py-1 font-bold text-gray-600">Time</td>
                  <td className="px-2 py-1 text-center font-mono text-gray-900">{stopSummary.fault.time}</td>
                  <td className="px-2 py-1 text-center font-mono text-gray-900">{stopSummary.manual.time}</td>
                  <td className="px-2 py-1 text-center font-mono text-gray-900">{stopSummary.other.time}</td>
                  <td className="px-2 py-1 text-center font-mono font-bold text-white bg-gray-700">{stopSummary.total.time}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <button
            onClick={() => { acknowledgeStop(); loadStopEvents(machine.id); }}
            disabled={loadingStopEvents === machine.id}
            className="w-full text-[10px] font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded py-1.5 hover:bg-blue-100 transition-colors disabled:opacity-50"
          >
            {loadingStopEvents === machine.id ? (
              <span className="flex items-center justify-center gap-1">
                <span className="animate-spin rounded-full h-3 w-3 border border-blue-300 border-t-blue-600"></span>
                Loading...
              </span>
            ) : (
              'Load Stop Details'
            )}
          </button>
        )}
      </div>

      {/* Footer - Last Update */}
      <div className="px-3 py-1.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <span className="text-[10px] text-gray-500">Last Update</span>
        <span className="text-[10px] font-mono text-gray-700">{machine.lastUpdate || 'N/A'}</span>
      </div>
    </div>
  );
};

export default RapierMachine;
